import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useDebounce } from '../hooks/useDebounce';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Scissors, Users as UsersIcon, IndianRupee, ClipboardList, BarChart3,
  Search, Plus, Pencil, Download, X, Trash2, History,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'tailors', label: 'Tailors', icon: UsersIcon },
  { id: 'prices', label: 'Stitching Prices', icon: IndianRupee },
  { id: 'work', label: 'Tailor Work', icon: ClipboardList },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Thumb = ({ src, alt }) => (
  src
    ? <img src={src} alt={alt} className="w-10 h-10 object-cover rounded border border-slate-200" loading="lazy" />
    : <div className="w-10 h-10 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-[10px] text-slate-400">—</div>
);

// Reusable server-side product picker (debounced)
const ProductPicker = ({ onSelect, testidPrefix }) => {
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 300);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (term) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (term.trim()) params.set('search', term.trim());
      const r = await axios.get(`${API}/tailors/product-search?${params}`);
      setResults(Array.isArray(r.data) ? r.data : []);
    } catch { setResults([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { run(dq); }, [dq, run]);

  return (
    <div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search design number / SKU / product..."
          data-testid={`${testidPrefix}-search`}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
        />
      </div>
      <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto mt-2" data-testid={`${testidPrefix}-results`}>
        {loading ? <div className="px-4 py-5 text-center text-sm text-slate-400">Searching…</div>
          : results.length === 0 ? <div className="px-4 py-5 text-center text-sm text-slate-400">No products</div>
          : (
            <ul className="divide-y divide-slate-100">
              {results.map((p) => (
                <li key={p.product_id}>
                  <button type="button" onClick={() => onSelect(p)}
                    data-testid={`${testidPrefix}-option-${p.design_number}`}
                    className="w-full flex items-center gap-3 text-left px-3 py-2 hover:bg-slate-50">
                    <Thumb src={p.thumbnail} alt={p.design_number} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{p.design_number}</div>
                      <div className="text-xs text-slate-500 truncate">{p.product_name}
                        {p.stitching_price != null && <span className="ml-2 text-indigo-600">· {money(p.stitching_price)}</span>}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
};

// ---------------- Dashboard ----------------
const DashboardTab = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/tailors/dashboard`).then(r => setD(r.data)).catch(() => {}); }, []);
  const cards = [
    { label: 'Active Tailors', value: `${d?.active_tailors ?? '—'} / ${d?.total_tailors ?? '—'}` },
    { label: 'Priced SKUs', value: d?.priced_skus ?? '—' },
    { label: 'Work Entries', value: d?.work_entries ?? '—' },
    { label: 'Total Pieces', value: d?.total_pieces ?? '—' },
    { label: 'Total Payable', value: d ? money(d.total_payable) : '—' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="tailors-dashboard">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase text-slate-500">{c.label}</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{c.value}</p>
        </div>
      ))}
    </div>
  );
};

// ---------------- Tailors ----------------
const TailorsTab = () => {
  const [tailors, setTailors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/tailors/tailors`); setTailors(Array.isArray(r.data) ? r.data : []); }
    catch { toast.error('Failed to load tailors'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setName(''); setActive(true); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setName(t.name); setActive(t.is_active); setShowForm(true); };

  const save = async () => {
    if (!name.trim()) { toast.error('Tailor name/ID is required'); return; }
    try {
      if (editing) await axios.put(`${API}/tailors/tailors/${editing.id}`, { name, is_active: active });
      else await axios.post(`${API}/tailors/tailors`, { name, is_active: active });
      toast.success('Saved'); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const toggle = async (t) => {
    try { await axios.put(`${API}/tailors/tailors/${t.id}`, { is_active: !t.is_active }); load(); }
    catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-4" data-testid="tailors-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800">Tailors</h2>
        <button onClick={openNew} data-testid="add-tailor-btn" className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">
          <Plus className="w-4 h-4" /> Add Tailor
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="tailor-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tailor Name / ID</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh, Tailor 01, A12"
              data-testid="tailor-name-input"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="tailor-active-checkbox" />
            Active
          </label>
          <div className="flex gap-2">
            <button onClick={save} data-testid="save-tailor-btn" className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-white border border-slate-200 rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr><th className="px-4 py-2 text-left">Name / ID</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tailors.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-8">No tailors yet</td></tr>}
            {tailors.map((t) => (
              <tr key={t.id} data-testid={`tailor-row-${t.name}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{t.name}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${t.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right space-x-2">
                  <button onClick={() => openEdit(t)} data-testid={`edit-tailor-${t.name}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => toggle(t)} data-testid={`toggle-tailor-${t.name}`} className="text-xs text-indigo-600 hover:underline">{t.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const fmtDT = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
const ACTION_LABEL = { created: 'Created', edited: 'Edited', deleted: 'Deleted', price_created: 'Price Created', price_changed: 'Price Changed', price_deleted: 'Price Deleted' };
const ACTION_CLS = { created: 'bg-emerald-50 text-emerald-700', edited: 'bg-amber-50 text-amber-700', deleted: 'bg-rose-50 text-rose-700', price_created: 'bg-emerald-50 text-emerald-700', price_changed: 'bg-amber-50 text-amber-700', price_deleted: 'bg-rose-50 text-rose-700' };

const Pager = ({ page, totalPages, setPage }) => (
  <div className="flex items-center justify-between p-3 text-sm">
    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 border border-slate-200 rounded-md disabled:opacity-40">Prev</button>
    <span className="text-slate-500">Page {page} / {totalPages}</span>
    <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 border border-slate-200 rounded-md disabled:opacity-40">Next</button>
  </div>
);

// ---------------- Price History ----------------
const PriceHistoryView = () => {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [search, setSearch] = useState(''); const dSearch = useDebounce(search, 300);
  const [items, setItems] = useState([]); const [page, setPage] = useState(1); const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), per_page: '25' });
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    if (dSearch.trim()) params.set('search', dSearch.trim());
    try { const r = await axios.get(`${API}/tailors/price-history?${params}`); setItems(r.data.items || []); setTotalPages(r.data.total_pages || 1); }
    catch { toast.error('Failed to load price history'); }
  }, [page, from, to, dSearch]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [from, to, dSearch]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="price-history-view">
      <div className="p-3 border-b border-slate-100 grid md:grid-cols-3 gap-2">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="price-history-from" className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="price-history-to" className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU / design..." data-testid="price-history-search" className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr><th className="px-3 py-2 text-left">Date / Time</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">By</th><th className="px-3 py-2 text-left">Img</th><th className="px-3 py-2 text-left">Design / SKU</th><th className="px-3 py-2 text-right">Previous</th><th className="px-3 py-2 text-right">New</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">No price history</td></tr>}
            {items.map((h) => (
              <tr key={h.id} data-testid={`price-history-row-${h.id}`}>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDT(h.performed_at)}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${ACTION_CLS[h.action] || 'bg-slate-100 text-slate-600'}`}>{ACTION_LABEL[h.action] || h.action}</span></td>
                <td className="px-3 py-2 text-slate-600">{h.performed_by_name}</td>
                <td className="px-3 py-2"><Thumb src={h.thumbnail} alt={h.design_number} /></td>
                <td className="px-3 py-2"><div className="font-medium text-slate-800">{h.design_number}</div><div className="text-xs text-slate-500">{h.product_name}</div></td>
                <td className="px-3 py-2 text-right text-slate-500">{h.previous_price != null ? money(h.previous_price) : '—'}</td>
                <td className="px-3 py-2 text-right font-medium">{h.new_price != null ? money(h.new_price) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
};

// ---------------- Stitching Prices ----------------
const PricesTab = () => {
  const [view, setView] = useState('prices'); // prices | history
  const [selected, setSelected] = useState(null);
  const [price, setPrice] = useState('');
  const [search, setSearch] = useState('');
  const dSearch = useDebounce(search, 300);
  const [items, setItems] = useState([]);

  const load = useCallback(async (term) => {
    try {
      const params = new URLSearchParams({ per_page: '50' });
      if (term.trim()) params.set('search', term.trim());
      const r = await axios.get(`${API}/tailors/stitching-prices?${params}`);
      setItems(r.data.items || []);
    } catch { toast.error('Failed to load prices'); }
  }, []);
  useEffect(() => { load(dSearch); }, [dSearch, load]);

  const save = async () => {
    if (!selected) { toast.error('Select a product first'); return; }
    const p = parseFloat(price);
    if (isNaN(p) || p < 0) { toast.error('Enter a valid price'); return; }
    try {
      await axios.post(`${API}/tailors/stitching-prices`, { product_id: selected.product_id, price: p });
      toast.success('Stitching price saved'); setSelected(null); setPrice(''); load(dSearch);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  return (
    <div className="space-y-4" data-testid="prices-tab">
      <div className="inline-flex bg-slate-100 rounded-lg p-1">
        <button onClick={() => setView('prices')} data-testid="prices-view-prices" className={`px-3 py-1.5 text-sm rounded-md ${view === 'prices' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Prices</button>
        <button onClick={() => setView('history')} data-testid="prices-view-history" className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${view === 'history' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}><History className="w-4 h-4" /> Price History</button>
      </div>

      {view === 'history' ? <PriceHistoryView /> : (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800">Set / Update Stitching Price</h3>
            {!selected ? (
              <ProductPicker testidPrefix="price-product" onSelect={(p) => { setSelected(p); setPrice(p.stitching_price != null ? String(p.stitching_price) : ''); }} />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-2 bg-indigo-50 border border-indigo-200 rounded-md" data-testid="price-selected-product">
                  <Thumb src={selected.thumbnail} alt={selected.design_number} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{selected.design_number}</div>
                    <div className="text-xs text-slate-500 truncate">{selected.product_name}</div>
                  </div>
                  <button onClick={() => { setSelected(null); setPrice(''); }} className="text-indigo-500 hover:text-indigo-700"><X className="w-4 h-4" /></button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Stitching Price per piece (₹)</label>
                  <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                    data-testid="stitching-price-input"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base" />
                </div>
                <button onClick={save} data-testid="save-price-btn" className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm">Save Price</button>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-3 border-b border-slate-100">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search saved prices..."
                  data-testid="price-list-search"
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
              </div>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr><th className="px-3 py-2 text-left">Img</th><th className="px-3 py-2 text-left">Design / SKU</th><th className="px-3 py-2 text-right">Price</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-8">No stitching prices set</td></tr>}
                  {items.map((it) => (
                    <tr key={it.product_id} data-testid={`price-row-${it.design_number}`}>
                      <td className="px-3 py-2"><Thumb src={it.thumbnail} alt={it.design_number} /></td>
                      <td className="px-3 py-2"><div className="font-medium text-slate-800">{it.design_number}</div><div className="text-xs text-slate-500">{it.product_name}</div></td>
                      <td className="px-3 py-2 text-right font-medium">{money(it.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Work History ----------------
const WorkHistoryView = () => {
  const [tailors, setTailors] = useState([]);
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [tailorId, setTailorId] = useState(''); const [product, setProduct] = useState(null);
  const [action, setAction] = useState('');
  const [items, setItems] = useState([]); const [page, setPage] = useState(1); const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { axios.get(`${API}/tailors/tailors`).then(r => setTailors(r.data || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), per_page: '25' });
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    if (tailorId) params.set('tailor_id', tailorId);
    if (product) params.set('product_id', product.product_id);
    if (action) params.set('action', action);
    try { const r = await axios.get(`${API}/tailors/work-history?${params}`); setItems(r.data.items || []); setTotalPages(r.data.total_pages || 1); }
    catch { toast.error('Failed to load work history'); }
  }, [page, from, to, tailorId, product, action]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [from, to, tailorId, product, action]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="work-history-view">
      <div className="p-3 border-b border-slate-100 grid md:grid-cols-5 gap-2 items-start">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="work-history-from" className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="work-history-to" className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <select value={tailorId} onChange={(e) => setTailorId(e.target.value)} data-testid="work-history-tailor" className="px-3 py-2 border border-slate-200 rounded-md text-sm">
          <option value="">All Tailors</option>
          {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="work-history-action" className="px-3 py-2 border border-slate-200 rounded-md text-sm">
          <option value="">All Actions</option>
          <option value="created">Created</option>
          <option value="edited">Edited</option>
          <option value="deleted">Deleted</option>
        </select>
        <div>
          {!product ? <ProductPicker testidPrefix="work-history-product" onSelect={setProduct} />
            : <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-md"><Thumb src={product.thumbnail} alt={product.design_number} /><span className="text-xs font-medium truncate flex-1">{product.design_number}</span><button onClick={() => setProduct(null)} className="text-indigo-500"><X className="w-4 h-4" /></button></div>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr><th className="px-3 py-2 text-left">Date / Time</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">By</th><th className="px-3 py-2 text-left">Tailor</th><th className="px-3 py-2 text-left">Design / SKU</th><th className="px-3 py-2 text-right">Pieces</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No history</td></tr>}
            {items.map((h) => {
              const prev = h.previous;
              return (
                <tr key={h.id} data-testid={`work-history-row-${h.id}`}>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDT(h.performed_at)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${ACTION_CLS[h.action] || 'bg-slate-100 text-slate-600'}`}>{ACTION_LABEL[h.action] || h.action}</span></td>
                  <td className="px-3 py-2 text-slate-600">{h.performed_by_name}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{h.tailor_name}</td>
                  <td className="px-3 py-2"><div className="font-medium text-slate-800">{h.design_number}</div><div className="text-xs text-slate-500">{h.product_name}</div></td>
                  <td className="px-3 py-2 text-right">{h.pieces}{prev && prev.pieces !== h.pieces && <div className="text-xs text-amber-600">was {prev.pieces}</div>}</td>
                  <td className="px-3 py-2 text-right">{money(h.stitching_price)}{prev && prev.stitching_price !== h.stitching_price && <div className="text-xs text-amber-600">was {money(prev.stitching_price)}</div>}</td>
                  <td className="px-3 py-2 text-right font-medium">{money(h.total_amount)}{prev && prev.total_amount !== h.total_amount && <div className="text-xs text-amber-600">was {money(prev.total_amount)}</div>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
};

// ---------------- Tailor Work ----------------
const WorkTab = () => {
  const [view, setView] = useState('entries'); // entries | history
  const [tailors, setTailors] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tailorId, setTailorId] = useState('');
  const [product, setProduct] = useState(null);
  const [price, setPrice] = useState('');
  const [pieces, setPieces] = useState('');

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fSearch, setFSearch] = useState('');
  const dSearch = useDebounce(fSearch, 300);

  const [editing, setEditing] = useState(null);
  const [eDate, setEDate] = useState(''); const [eTailor, setETailor] = useState(''); const [ePieces, setEPieces] = useState(''); const [ePrice, setEPrice] = useState('');

  useEffect(() => { axios.get(`${API}/tailors/tailors?active_only=true`).then(r => setTailors(r.data || [])).catch(() => {}); }, []);

  const loadWork = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '25' });
      if (dSearch.trim()) params.set('search', dSearch.trim());
      const r = await axios.get(`${API}/tailors/work?${params}`);
      setItems(r.data.items || []); setTotalPages(r.data.total_pages || 1);
    } catch { toast.error('Failed to load work'); }
  }, [page, dSearch]);
  useEffect(() => { loadWork(); }, [loadWork]);
  useEffect(() => { setPage(1); }, [dSearch]);

  const total = (parseFloat(price) || 0) * (parseInt(pieces) || 0);

  const save = async () => {
    if (!tailorId) { toast.error('Select a tailor'); return; }
    if (!product) { toast.error('Select a design/SKU'); return; }
    if (!pieces || parseInt(pieces) <= 0) { toast.error('Enter pieces'); return; }
    try {
      await axios.post(`${API}/tailors/work`, {
        date, tailor_id: tailorId, product_id: product.product_id,
        pieces: parseInt(pieces),
        stitching_price: price === '' ? null : parseFloat(price),
      });
      toast.success('Work entry saved');
      setProduct(null); setPrice(''); setPieces('');
      loadWork();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const openEdit = (w) => { setEditing(w); setEDate(w.date || ''); setETailor(w.tailor_id || ''); setEPieces(String(w.pieces)); setEPrice(String(w.stitching_price)); };
  const saveEdit = async () => {
    if (!ePieces || parseInt(ePieces) <= 0) { toast.error('Enter pieces'); return; }
    try {
      await axios.put(`${API}/tailors/work/${editing.id}`, {
        date: eDate, tailor_id: eTailor,
        pieces: parseInt(ePieces),
        stitching_price: ePrice === '' ? null : parseFloat(ePrice),
      });
      toast.success('Work entry updated'); setEditing(null); loadWork();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Delete this work entry? It will be kept in history.`)) return;
    try { await axios.delete(`${API}/tailors/work/${w.id}`); toast.success('Work entry deleted'); loadWork(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };
  const eTotal = (parseFloat(ePrice) || 0) * (parseInt(ePieces) || 0);

  return (
    <div className="space-y-4" data-testid="work-tab">
      <div className="inline-flex bg-slate-100 rounded-lg p-1">
        <button onClick={() => setView('entries')} data-testid="work-view-entries" className={`px-3 py-1.5 text-sm rounded-md ${view === 'entries' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Entries</button>
        <button onClick={() => setView('history')} data-testid="work-view-history" className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${view === 'history' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}><History className="w-4 h-4" /> History</button>
      </div>

      {view === 'history' ? <WorkHistoryView /> : (
      <>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">New Work Entry</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="work-date-input"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tailor</label>
              <select value={tailorId} onChange={(e) => setTailorId(e.target.value)} data-testid="work-tailor-select"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base">
                <option value="">Choose tailor...</option>
                {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Design Number / SKU</label>
            {!product ? (
              <ProductPicker testidPrefix="work-product" onSelect={(p) => { setProduct(p); setPrice(p.stitching_price != null ? String(p.stitching_price) : '0'); }} />
            ) : (
              <div className="flex items-center gap-3 p-2 bg-indigo-50 border border-indigo-200 rounded-md" data-testid="work-selected-product">
                <Thumb src={product.thumbnail} alt={product.design_number} />
                <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-800 truncate">{product.design_number}</div><div className="text-xs text-slate-500 truncate">{product.product_name}</div></div>
                <button onClick={() => { setProduct(null); setPrice(''); }} className="text-indigo-500 hover:text-indigo-700"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-4 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Stitching Price (₹)</label>
            <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="work-price-input"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Pieces Completed</label>
            <input type="number" min="1" value={pieces} onChange={(e) => setPieces(e.target.value)} data-testid="work-pieces-input"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base" />
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Total Amount</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="work-total-preview">{money(total)}</p>
          </div>
        </div>
        <button onClick={save} data-testid="save-work-btn" className="mt-4 px-5 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium">Save Work Entry</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Search by tailor / design / product..."
              data-testid="work-list-search"
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Tailor</th>
                <th className="px-3 py-2 text-left">Img</th><th className="px-3 py-2 text-left">Design / SKU</th>
                <th className="px-3 py-2 text-right">Pieces</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No work entries</td></tr>}
              {items.map((w) => (
                <tr key={w.id} data-testid={`work-row-${w.id}`}>
                  <td className="px-3 py-2 text-slate-600">{w.date}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{w.tailor_name}</td>
                  <td className="px-3 py-2"><Thumb src={w.thumbnail} alt={w.design_number} /></td>
                  <td className="px-3 py-2"><div className="font-medium text-slate-800">{w.design_number}</div><div className="text-xs text-slate-500">{w.product_name}</div></td>
                  <td className="px-3 py-2 text-right">{w.pieces}</td>
                  <td className="px-3 py-2 text-right">{money(w.stitching_price)}</td>
                  <td className="px-3 py-2 text-right font-medium">{money(w.total_amount)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(w)} data-testid={`edit-work-${w.id}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 mr-2"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => remove(w)} data-testid={`delete-work-${w.id}`} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager page={page} totalPages={totalPages} setPage={setPage} />
      </div>
      </>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="edit-work-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-semibold text-slate-800">Edit Work Entry</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-3 p-2 bg-slate-50 border border-slate-200 rounded-md">
              <Thumb src={editing.thumbnail} alt={editing.design_number} />
              <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{editing.design_number}</div><div className="text-xs text-slate-500 truncate">{editing.product_name}</div></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={eDate} onChange={(e) => setEDate(e.target.value)} data-testid="edit-work-date" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tailor</label>
              <select value={eTailor} onChange={(e) => setETailor(e.target.value)} data-testid="edit-work-tailor" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                {!tailors.some((t) => t.id === eTailor) && <option value={eTailor}>{editing.tailor_name} (inactive)</option>}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pieces</label>
                <input type="number" min="1" value={ePieces} onChange={(e) => setEPieces(e.target.value)} data-testid="edit-work-pieces" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Price (₹)</label>
                <input type="number" min="0" step="0.01" value={ePrice} onChange={(e) => setEPrice(e.target.value)} data-testid="edit-work-price" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
            </div>
            <div className="text-right text-sm text-slate-600">New Total: <span className="font-bold text-slate-900" data-testid="edit-work-total">{money(eTotal)}</span></div>
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
              <button onClick={saveEdit} data-testid="save-edit-work-btn" className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Reports ----------------
const ReportsTab = () => {
  const [tailors, setTailors] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tailorId, setTailorId] = useState('');
  const [product, setProduct] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { axios.get(`${API}/tailors/tailors`).then(r => setTailors(r.data || [])).catch(() => {}); }, []);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    if (tailorId) params.set('tailor_id', tailorId);
    if (product) params.set('product_id', product.product_id);
    return params;
  };

  const run = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/tailors/reports?${buildParams()}`); setReport(r.data); }
    catch { toast.error('Failed to generate report'); } finally { setLoading(false); }
  };

  const exportPdf = async () => {
    try {
      const r = await axios.get(`${API}/tailors/reports/pdf?${buildParams()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'tailor_report.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export PDF'); }
  };

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div><label className="block text-xs text-slate-600 mb-1">From Date</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" /></div>
          <div><label className="block text-xs text-slate-600 mb-1">To Date</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" /></div>
          <div><label className="block text-xs text-slate-600 mb-1">Tailor</label>
            <select value={tailorId} onChange={(e) => setTailorId(e.target.value)} data-testid="report-tailor" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
              <option value="">All Tailors</option>
              {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></div>
          <div><label className="block text-xs text-slate-600 mb-1">SKU / Design</label>
            {!product ? (
              <div className="text-xs"><ProductPicker testidPrefix="report-product" onSelect={setProduct} /></div>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-md">
                <Thumb src={product.thumbnail} alt={product.design_number} />
                <span className="text-xs font-medium truncate flex-1">{product.design_number}</span>
                <button onClick={() => setProduct(null)} className="text-indigo-500"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={run} data-testid="run-report-btn" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">{loading ? 'Generating…' : 'Generate Report'}</button>
          <button onClick={exportPdf} data-testid="export-pdf-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md text-sm hover:bg-slate-50"><Download className="w-4 h-4" /> Export PDF</button>
        </div>
      </div>

      {report && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="report-result">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Img</th><th className="px-3 py-2 text-left">Tailor</th>
                  <th className="px-3 py-2 text-left">Design / SKU</th><th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Pieces</th><th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {report.rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8">No records for these filters</td></tr>}
                {report.rows.map((r, i) => (
                  <tr key={i} data-testid="report-row">
                    <td className="px-3 py-2"><Thumb src={r.thumbnail} alt={r.design_number} /></td>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.tailor_name}</td>
                    <td className="px-3 py-2"><div className="font-medium text-slate-800">{r.design_number}</div><div className="text-xs text-slate-500">{r.product_name}</div></td>
                    <td className="px-3 py-2 text-right">{money(r.stitching_price)}</td>
                    <td className="px-3 py-2 text-right">{r.total_pieces}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(r.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100 font-semibold">
                <tr>
                  <td className="px-3 py-2" colSpan={4}>TOTAL</td>
                  <td className="px-3 py-2 text-right" data-testid="report-total-pieces">{report.total_pieces}</td>
                  <td className="px-3 py-2 text-right" data-testid="report-total-payable">{money(report.total_payable)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Page ----------------
export const Tailors = () => {
  const [tab, setTab] = useState('dashboard');
  return (
    <Layout>
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="tailors-title">
          <Scissors className="w-7 h-7 text-indigo-600" /> Tailors
        </h1>
        <p className="mt-1 text-sm sm:text-base text-slate-600">Stitching prices &amp; tailor payment tracking (admin only)</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tailors-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'tailors' && <TailorsTab />}
      {tab === 'prices' && <PricesTab />}
      {tab === 'work' && <WorkTab />}
      {tab === 'reports' && <ReportsTab />}
    </Layout>
  );
};

export default Tailors;
